import React, { useState, useEffect, useCallback, ReactNode, RefObject, useRef } from 'react';
import Popper, { PopperPlacementType } from '@mui/material/Popper';
import { styled } from '@mui/system';
import Fade from '@mui/material/Fade';

type HintProps = {
  anchor: RefObject<HTMLElement | null>;
  showCondition?: (element: HTMLElement) => boolean;
  beforeHint?: (element: HTMLElement) => void;
  delay?: number;
  closeDelay?: number;
  fadeDuration?: number;
  placement?: PopperPlacementType,
  children: ReactNode | string,
  disabled?: boolean,
  bubbledEvents?: boolean,
  distance?: number,
  flipFallbackPlacements?: Array<PopperPlacementType>
};

const Paper = styled('div')({
  padding: '6px 10px 6px 10px',
  borderRadius: '5px',
  border: '1px solid rgba(0, 0, 0, 0.17)',
  boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.07)',
  background: '#FFFFFF'
});

export default function ({ 
  children, anchor, delay = 0, closeDelay = 0, fadeDuration = 100, placement = 'auto',
  showCondition, disabled = false, beforeHint, bubbledEvents = false,
  distance = 0, flipFallbackPlacements = [ 'right', 'bottom', 'left', 'top' ]
}: HintProps) {
  const [isShown, setIsShown] = useState<boolean>(false);
  const [lastHandler, setLastHandler] = useState<number | null>(null);

  useEffect(() => {
    if (lastHandler) {
      return () => clearTimeout(lastHandler);
    }
  }, [ lastHandler ]);

  const close = () => {
    setIsShown(false);
    setLastHandler(null);
  };

  const open = useCallback(() => {
    if (beforeHint && anchor.current) {
      beforeHint(anchor.current);
    }
    setIsShown(true);
    setLastHandler(null);
  }, [ anchor.current ]);


  useEffect(() => {
    if (!!anchor.current) {
      const element = anchor.current;

      const delayedOpen = () => {
        if (!showCondition || showCondition(element)) {
          setLastHandler(
            window.setTimeout(open, delay)
          );
        }
      }
      
      const delayedClose = () => {
        setLastHandler(
          window.setTimeout(close, closeDelay)
        );
      }

      element.addEventListener(bubbledEvents ? 'mouseover' : 'mouseenter', delayedOpen);
      element.addEventListener(bubbledEvents ? 'mouseout' : 'mouseleave', delayedClose);
      return () => {
        element.removeEventListener(bubbledEvents ? 'mouseover' : 'mouseenter', delayedOpen);
        element.removeEventListener(bubbledEvents ? 'mouseout' : 'mouseleave', delayedClose);
      }
    }
  }, [ anchor.current, showCondition ]);

  useEffect(() => {
    if (disabled) {
      close();
    }
  }, [ disabled ]);

  return anchor.current && (
    <Popper
      anchorEl={anchor.current}
      open={isShown && !disabled}
      placement={placement}
      transition
      sx={{
        zIndex: 9999
      }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, distance],
          },
        },
        {
          name: 'flip',
          enabled: true,
          options: {
            fallbackPlacements: flipFallbackPlacements,
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            boundary: 'viewport',
          },
        },
      ]}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={fadeDuration}>
          <Paper>
            {children}
          </Paper>
        </Fade>
      )}
    </Popper>
  );
}
